import { boardSaveErrorMessage } from "./boardErrors.js";

export const OPS_RETRY_BASE_MS = 450;
export const OPS_MAX_TRANSIENT_ATTEMPTS = 5;
export const OPS_MAX_CONFLICT_ATTEMPTS = 5;

export type OpsErrLike = {
  code?: string;
  message?: string;
  currentRevision?: number;
} | null | undefined;

export function isAddOnlyOps(ops: Array<{ type?: string }> | null | undefined): boolean {
  if (!ops || !ops.length) return false;
  for (let i = 0; i < ops.length; i++) {
    if (!ops[i] || ops[i]!.type !== "add_object") return false;
  }
  return true;
}

/** Сетевые/временные сбои — повторяем без отката optimistic UI. */
export function isTransientOpsError(err: OpsErrLike): boolean {
  const code = err?.code ? String(err.code) : "";
  const msg = err?.message ? String(err.message) : "";
  if (code === "revision_conflict") return false;
  if (
    code === "invalid_input" ||
    code === "op_rejected" ||
    code === "forbidden" ||
    code === "schema_outdated" ||
    code === "not_found"
  ) {
    return false;
  }
  if (code === "internal_error") return true;
  // Сервер притормозил поток ops с этого соединения — повторяем с паузой.
  if (code === "rate_limited") return true;
  if (code === "ops_ws_fail") return true;
  if (msg.includes("ops_ack timeout")) return true;
  if (msg.includes("WebSocket not ready")) return true;
  if (msg === "Failed to fetch" || msg.includes("NetworkError")) return true;
  if (msg.includes("Request failed")) return true;
  if (msg === "Invalid server response") return true;
  return false;
}

export function isRetryableRevisionConflict(
  err: OpsErrLike,
  ops: Array<{ type?: string }> | null | undefined
): boolean {
  return err?.code === "revision_conflict" && isAddOnlyOps(ops);
}

/**
 * Поля виджетов, которые синхронизируются по мере набора («Код», «SQL»).
 * Для них «кто написал последним, тот и прав» — нормальное поведение:
 * так работает живой набор у всех участников.
 */
const LIVE_TEXT_FIELDS = new Set([
  "source",
  "html",
  "css",
  "js",
  "stdout",
  "runSeq",
  "query",
  "setupSql",
  "customPrompt",
  "customSolution",
  "status",
  "output",
]);

/** Пакет — только правки живых текстовых полей виджетов. */
export function isLiveTextOps(
  ops: Array<{ type?: string; patch?: unknown }> | null | undefined
): boolean {
  if (!ops || !ops.length) return false;
  for (const op of ops) {
    if (!op || op.type !== "update_object") return false;
    const patch = op.patch;
    if (!patch || typeof patch !== "object") return false;
    const keys = Object.keys(patch);
    if (!keys.length || !keys.every((k) => LIVE_TEXT_FIELDS.has(k))) return false;
  }
  return true;
}

/**
 * Двое одновременно печатали в одном поле виджета: сервер отклонил позднюю
 * правку. Вместо ошибки на экране перечитываем доску и досылаем свой текст.
 */
export function isRetryableLiveTextConflict(
  err: OpsErrLike,
  ops: Array<{ type?: string; patch?: unknown }> | null | undefined
): boolean {
  return err?.code === "revision_conflict" && isLiveTextOps(ops);
}

export function opsRetryDelayMs(attempt: number): number {
  return OPS_RETRY_BASE_MS * Math.max(1, attempt);
}

export function delayMs(ms: number): Promise<void> {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

/** Понятный текст для тоста + технический хвост для поддержки и bug report. */
export function formatBoardOpsErrorToast(err: OpsErrLike, context?: string): string {
  const userMsg = boardSaveErrorMessage(err);
  const code = err?.code ? String(err.code) : "";
  const raw = err?.message ? String(err.message).trim().slice(0, 140) : "";
  const tech: string[] = [];
  if (context) tech.push(context);
  if (code) tech.push("code=" + code);
  if (raw && raw !== userMsg && !userMsg.includes(raw)) tech.push(raw);
  if (!tech.length) return userMsg;
  return userMsg + " [" + tech.join(" · ") + "]";
}
